/**
 * Single client-side error reporting funnel.
 *
 * Every captured error (React error boundary, window "error" /
 * "unhandledrejection") flows through reportError, which forwards to Sentry
 * (when initialized) and relays a sanitized payload to
 * POST /api/public/client-errors. The server logs every report to Loki and
 * forwards only critical ones to Slack.
 * Reporting failures fall back to console.error and never recurse.
 */
import * as Sentry from "@sentry/react";

// Rate limiting for error notifications
class ErrorRateLimit {
  private static errorCounts = new Map<
    string,
    { count: number; lastSent: number }
  >();
  private static readonly MAX_ERRORS_PER_HOUR = 10;
  private static readonly HOUR_MS = 60 * 60 * 1000;

  static shouldSendError(errorSignature: string): boolean {
    const now = Date.now();
    const current = this.errorCounts.get(errorSignature) || {
      count: 0,
      lastSent: 0,
    };

    // Reset count if it's been over an hour
    if (now - current.lastSent > this.HOUR_MS) {
      current.count = 0;
    }

    if (current.count >= this.MAX_ERRORS_PER_HOUR) {
      return false;
    }

    current.count++;
    current.lastSent = now;
    this.errorCounts.set(errorSignature, current);

    return true;
  }
}

// Errors with zero diagnostic value that are dropped before reporting.
// Everything else is sent to the backend, which logs all reports to Loki and
// forwards only critical ones to Slack (see server/routes/public/post-client-error.ts)
// — so network/chunk-load failures stay queryable without paging anyone.
class ErrorFilter {
  private static readonly IGNORED_ERRORS = [
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications",
    "Non-Error promise rejection captured",
  ];

  static shouldIgnoreError(error: Error): boolean {
    // Match against name and message: webpack puts "ChunkLoadError" in
    // error.name, browsers vary on where they surface identifiers.
    const haystack = `${error.name || ""} ${error.message || ""}`.toLowerCase();
    return this.IGNORED_ERRORS.some((ignored) =>
      haystack.includes(ignored.toLowerCase()),
    );
  }
}

function toError(error: unknown): Error {
  if (error instanceof Error) return error;
  return new Error(typeof error === "string" ? error : String(error));
}

function getUserContext() {
  try {
    // Try to get user info from Clerk's global state (safer than window access)
    let userInfo: any = { context: "No user info available" };

    // Check for Clerk session data in localStorage (following app patterns)
    const clerkSession = localStorage.getItem("__session");
    if (clerkSession) {
      userInfo.hasSession = true;
    }

    // Try to access Clerk's current user (if available in DOM)
    if (typeof window !== "undefined" && (window as any).__clerk) {
      const clerk = (window as any).__clerk;
      if (clerk.user) {
        userInfo = {
          id: clerk.user.id,
          email: clerk.user.primaryEmailAddress?.emailAddress,
          firstName: clerk.user.firstName,
          lastName: clerk.user.lastName,
          orgId: clerk.organization?.id,
          orgName: clerk.organization?.name,
          hasSession: true,
        };
      }
    }

    // Add browser context
    userInfo.viewport = `${window.innerWidth}x${window.innerHeight}`;
    userInfo.timestamp = new Date().toISOString();

    return userInfo;
  } catch (error) {
    return {
      context: "Error accessing user context",
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}

function sanitizeContext(
  context?: Record<string, unknown>,
): Record<string, any> {
  if (!context) return {};

  // Deep clone to avoid cyclic references and sanitize
  const sanitized: Record<string, any> = {};
  const sensitiveKeys = [
    "password",
    "token",
    "key",
    "secret",
    "auth",
    "credit",
    "ssn",
  ];

  Object.keys(context).forEach((key) => {
    try {
      let value = context[key];

      // Handle sensitive keys
      if (
        sensitiveKeys.some((sensitive) => key.toLowerCase().includes(sensitive))
      ) {
        sanitized[key] = "[REDACTED]";
        return;
      }

      // Handle different value types safely
      if (value === null || value === undefined) {
        sanitized[key] = value;
      } else if (typeof value === "string") {
        sanitized[key] =
          value.length > 1000
            ? value.substring(0, 1000) + "...[TRUNCATED]"
            : value;
      } else if (typeof value === "number" || typeof value === "boolean") {
        sanitized[key] = value;
      } else if (typeof value === "object") {
        // Avoid cyclic references by converting to string representation
        try {
          sanitized[key] = JSON.parse(JSON.stringify(value));
        } catch (cyclicError) {
          sanitized[key] = "[CYCLIC_OBJECT]";
        }
      } else if (typeof value === "function") {
        sanitized[key] = "[FUNCTION]";
      } else {
        sanitized[key] = String(value);
      }
    } catch (error) {
      sanitized[key] = "[SERIALIZATION_ERROR]";
    }
  });

  return sanitized;
}

async function sendToBackend(errorPayload: any) {
  try {
    const response = await fetch("/api/public/client-errors", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(errorPayload),
    });

    if (!response.ok) {
      console.error("Client error report failed:", response.status);
    }
  } catch {
    // Silent fail - don't want error reporting to break the app
  }
}

/**
 * Report an error to Sentry (when initialized) and relay it to the backend
 * error funnel (Loki always, Slack when critical). Filtered and rate-limited
 * (10/hr per error signature). Never throws — failures inside the reporting
 * path fall back to console.error.
 */
export function reportError(
  error: unknown,
  context?: Record<string, unknown>,
): void {
  try {
    const normalized = toError(error);

    // Filter out ignored errors
    if (ErrorFilter.shouldIgnoreError(normalized)) {
      console.log("Ignoring filtered error:", normalized.message);
      return;
    }

    // Create error signature for rate limiting
    const errorSignature = `${normalized.name}:${normalized.message?.substring(0, 100)}`;

    // Check rate limiting
    if (!ErrorRateLimit.shouldSendError(errorSignature)) {
      console.log("Rate limited error:", errorSignature);
      return;
    }

    const sanitizedContext = sanitizeContext(context);

    if (Sentry.isInitialized()) {
      Sentry.captureException(error, { extra: sanitizedContext });
    }

    const componentStack =
      typeof context?.componentStack === "string"
        ? context.componentStack
        : undefined;

    // Build error payload safely to avoid cyclic references. Field lengths
    // match the server's zod schema (server/routes/public/post-client-error.ts).
    const errorPayload = {
      message: (normalized.message || "Unknown error").substring(0, 1000),
      name: (normalized.name || "Error").substring(0, 200),
      stack: normalized.stack
        ? normalized.stack.substring(0, 5000)
        : "No stack trace available",
      componentStack: componentStack
        ? componentStack.substring(0, 2000)
        : "No component stack",
      timestamp: new Date().toISOString(),
      url: window.location.href.substring(0, 2000),
      userAgent: navigator.userAgent.substring(0, 500),
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      user: getUserContext(),
      context: sanitizedContext,
    };

    // Console log for development
    console.error("Client error captured:", errorPayload);

    // Send error report to the backend funnel (Loki + Slack) — fire-and-forget
    void sendToBackend(errorPayload);
  } catch (reportingError) {
    // Never recurse back into reportError from the reporting path itself
    console.error("Error reporting failed:", reportingError, error);
  }
}

let globalHandlersInstalled = false;

/**
 * Register window-level handlers for uncaught errors and unhandled promise
 * rejections. Call once at startup (main.tsx). Idempotent.
 */
export function initGlobalErrorHandlers(): void {
  if (globalHandlersInstalled) return;
  globalHandlersInstalled = true;

  window.addEventListener("error", (event: ErrorEvent) => {
    reportError(event.error ?? event.message, {
      source: "window.onerror",
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener(
    "unhandledrejection",
    (event: PromiseRejectionEvent) => {
      reportError(event.reason, { source: "unhandledrejection" });
    },
  );
}
